import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button, Container, Field, Input, Panel } from '../components/site/ui';
import { formatPrice } from '../components/plans';
import {
  IconArrowLeft,
  IconCheck,
  IconCheckCircle,
  IconLock,
  IconMail,
  IconPackage
} from '../components/icons';

/**
 * The two plans for this audience, side by side on the registration form.
 *
 * Registration is where an account is made, so the plan belongs here rather
 * than on a separate page per plan — someone who arrived on the wrong one can
 * change their mind without going back. A paid plan can also be marked as not
 * self-serve, in which case it is shown but not selectable and points at the
 * contact form instead; nothing uses that today, both paid plans are bought
 * here.
 */
const PlanChooser = ({ plans, paidPlan, allowance, contactTo, value, onChange }) => {
  if (!plans) return null;
  const paid = plans[paidPlan.key];
  if (!paid) return null;

  const options = [
    {
      key: 'pilot',
      label: 'Pilot',
      price: 'Free',
      hint: allowance ? `${allowance} free case${allowance === 1 ? '' : 's'}` : 'Free cases to try it',
      selectable: true
    },
    {
      key: paidPlan.key,
      label: paidPlan.label,
      price: paid.isCustom ? 'Custom' : `£${formatPrice(paid.price)}`,
      // Seat-priced plans read "per user / month" — the plan price buys the
      // first user and each extra one costs the same again. A flat plan
      // (courier Intelligence) is simply per month.
      hint: paid.isCustom ? 'Talk to us' : paidPlan.perUser ? 'per user / month' : 'per month',
      selectable: paidPlan.selfServe && !paid.isCustom
    }
  ];

  return (
    <div className="mt-5">
      <p className="text-sm font-600 text-ink-800">Choose your plan</p>
      <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const selected = value === option.key;
          // A card that can't be picked isn't a label for anything — it's a
          // panel with a link out, so clicking its "get in touch" works
          // instead of being swallowed by a label for a disabled input.
          const Tag = option.selectable ? 'label' : 'div';
          return (
            <Tag
              key={option.key}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3.5 transition-colors ${
                !option.selectable
                  ? 'cursor-default border-ink-200 bg-ink-50/60'
                  : selected
                    ? 'border-brand-800 bg-brand-50/60'
                    : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50'
              }`}
            >
              <input
                type="radio"
                name="signup-plan"
                value={option.key}
                checked={selected}
                disabled={!option.selectable}
                onChange={() => onChange(option.key)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand-800"
              />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline justify-between gap-x-2">
                  <span
                    className={`text-sm ${selected ? 'font-700 text-ink-900' : 'font-600 text-ink-800'}`}
                  >
                    {option.label}
                  </span>
                  <span className="font-display text-sm font-700 tabular-nums text-brand-800">
                    {option.price}
                  </span>
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-ink-500">
                  {option.selectable ? (
                    option.hint
                  ) : (
                    <>
                      Set up by our team —{' '}
                      <Link
                        to={contactTo}
                        className="font-600 text-brand-700 underline hover:text-brand-800"
                      >
                        get in touch
                      </Link>
                    </>
                  )}
                </span>
              </span>
            </Tag>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Merchant and courier registration are the same flow — a form that either
 * emails a confirmation link (free pilot) or goes to Stripe (paid plan) — so
 * they share a component rather than two near-identical files that drift
 * apart. What genuinely differs lives here: the endpoints, the identity
 * colour, the copy, and what happens once the account exists.
 *
 * Both audiences land on a password screen once their account exists — each
 * has a portal of its own to sign into — so the only real differences are the
 * endpoints, the copy and the identity colour.
 */
const AUDIENCES = {
  merchant: {
    eyebrow: 'For merchants',
    heading: ['Start your free', 'merchant pilot.'],
    paidHeading: ['Set up your', 'merchant account.'],
    accent: 'text-gold-600',
    chip: 'bg-gold-50 text-gold-700 ring-gold-100',
    dot: 'bg-gold-500 text-brand-900',
    intro:
      'Handle delivery issues that customers report against your store — refund, reship, replace or investigate, with the evidence already gathered.',
    requestUrl: '/api/merchants/pilot-signup',
    confirmUrl: '/api/merchants/pilot-signup/confirm',
    nameLabel: 'Business name',
    namePlaceholder: 'Your company',
    backTo: '/merchants',
    backLabel: 'Back to merchants',
    signInTo: '/merchant/login',
    pricingTo: '/pricing?for=merchants',
    contactTo: '/contact',
    pricingKey: 'merchantPlanPrices',
    /** The paid plan offered beside the pilot, and whether it can be bought here. */
    paidPlan: { key: 'merchant', label: 'Merchant', selfServe: true, perUser: true },
    planCheckoutUrl: '/api/stripe/plan-checkout',
    benefits: (allowance, features) => [
      allowance
        ? `${allowance} free case${allowance === 1 ? '' : 's'} to try it`
        : 'Free cases to try it',
      'No card required',
      ...features
    ],
    paidBenefits: (plan, seatPrice) => [
      plan?.includedCases == null
        ? 'Unlimited cases'
        : `${plan.includedCases.toLocaleString('en-GB')} cases per month`,
      seatPrice
        ? `Add users any time — £${formatPrice(seatPrice)} per user / month`
        : 'Add users any time',
      ...(Array.isArray(plan?.features) ? plan.features : [])
    ]
  },
  courier: {
    eyebrow: 'For couriers',
    heading: ['Start your free', 'courier pilot.'],
    paidHeading: ['Set up your', 'courier account.'],
    accent: 'text-info-600',
    chip: 'bg-info-50 text-info-600 ring-info-100',
    dot: 'bg-info-500 text-white',
    intro:
      'See the delivery problems customers are reporting against your network — issue trends, postcode hotspots and the retailers they cluster around.',
    requestUrl: '/api/couriers/pilot-signup',
    confirmUrl: '/api/couriers/pilot-signup/confirm',
    nameLabel: 'Company name',
    namePlaceholder: 'Your delivery company',
    backTo: '/couriers',
    backLabel: 'Back to couriers',
    signInTo: '/courier/login',
    pricingTo: '/pricing?for=couriers',
    contactTo: '/contact?topic=courier',
    pricingKey: 'courierPlanPrices',
    // Flat monthly fee, no seats and no case allowance — the plan sells depth
    // of data, so its configured features are the whole pitch.
    paidPlan: { key: 'intelligence', label: 'Intelligence', selfServe: true, perUser: false },
    planCheckoutUrl: '/api/couriers/plan-checkout',
    benefits: (allowance, features) => [
      allowance
        ? `${allowance} free case${allowance === 1 ? '' : 's'} to try it`
        : 'Free cases to try it',
      'No card required',
      ...features
    ],
    paidBenefits: (plan) => [
      'Every report naming your network',
      ...(Array.isArray(plan?.features) ? plan.features : [])
    ]
  }
};

const PilotSignupPage = ({ audience = 'merchant' }) => {
  const config = AUDIENCES[audience] || AUDIENCES.merchant;
  const { token } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', contactName: '', email: '', website: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [confirmed, setConfirmed] = useState(null);
  const [allowance, setAllowance] = useState(null);
  const [features, setFeatures] = useState([]);
  const [plans, setPlans] = useState(null);
  const [seatPrice, setSeatPrice] = useState(null);
  // Which plan they are signing up on. Pre-selected from ?plan= so arriving
  // from a pricing card lands on that card already chosen; pilot otherwise,
  // since that is the one anyone can start without paying.
  const [plan, setPlan] = useState(() => {
    const wanted = new URLSearchParams(window.location.search).get('plan');
    const paid = (AUDIENCES[audience] || AUDIENCES.merchant).paidPlan;
    return wanted === paid.key && paid.selfServe ? paid.key : 'pilot';
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const onPilot = plan === 'pilot';
  const paidConfig = plans?.[config.paidPlan.key] || null;
  const heading = onPilot ? config.heading : config.paidHeading;
  // The rail sells whichever plan is selected, so switching the chooser
  // switches what is promised beside it — a paid signup shouldn't still be
  // reading "no card required".
  const benefits = onPilot
    ? config.benefits(allowance, features)
    : config.paidBenefits(paidConfig, seatPrice);

  // Prices, allowances and selling points are all admin-configurable, so the
  // plan cards are built from the live configuration rather than written into
  // the copy here.
  useEffect(() => {
    let cancelled = false;
    const url = audience === 'courier' ? '/api/couriers/pricing' : '/api/merchants/pricing';
    api
      .get(url)
      .then(({ data }) => {
        if (cancelled) return;
        const all = data?.[config.pricingKey] || null;
        setPlans(all);
        if (typeof data?.seatPrice === 'number') setSeatPrice(data.seatPrice);
        const pilot = all?.pilot;
        const value =
          audience === 'courier' ? pilot?.freeCaseAllowance : pilot?.includedCases;
        if (typeof value === 'number') setAllowance(value);
        if (Array.isArray(pilot?.features)) setFeatures(pilot.features);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [audience, config.pricingKey]);

  // Which token a confirm request has already been sent for. Refs survive
  // React 18 StrictMode's synthetic double effect in development, so this stops
  // the dev-only double-invoke firing this mutation twice on every load. It
  // cannot stop a genuinely separate second request — a double click, two tabs,
  // a mail client prescanning the link — and the server tolerates those.
  const confirmedForRef = useRef(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!token) return undefined;
    isMountedRef.current = true;

    if (confirmedForRef.current !== token) {
      confirmedForRef.current = token;
      api
        .post(config.confirmUrl, { token })
        .then(({ data }) => {
          if (!isMountedRef.current) return;
          // Merchants are handed straight to a password screen; couriers have
          // nowhere to sign into yet, so they get a confirmation here.
          if (data?.setPasswordToken) {
            navigate(`/set-password/${data.setPasswordToken}`, { replace: true });
            return;
          }
          setConfirmed(data || { started: true });
        })
        .catch((err) => {
          if (!isMountedRef.current) return;
          setConfirmError(err.response?.data?.error || 'This link is invalid or has expired.');
        });
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [token, navigate, config.confirmUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      // The paid plan is bought here and now: Stripe collects payment and the
      // account is created from the completed checkout, so there is no
      // confirm-your-email round trip the way the free pilot has.
      if (plan !== 'pilot') {
        const { data } = await api.post(config.planCheckoutUrl, { plan, ...form });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        setErrors({ _general: 'Could not start checkout. Please try again.' });
        return;
      }

      await api.post(config.requestUrl, form);
      setSent(true);
    } catch (err) {
      const data = err.response?.data;
      const fieldErrors = {};
      if (Array.isArray(data?.errors)) {
        data.errors.forEach((e2) => {
          if (e2.path) fieldErrors[e2.path] = e2.msg;
        });
      }
      if (data?.error) fieldErrors._general = data.error;
      if (!Object.keys(fieldErrors).length) {
        fieldErrors._general = 'Could not start your pilot. Please try again.';
      }
      setErrors(fieldErrors);
    } finally {
      setLoading(false);
    }
  };

  /* ── Confirm half: /…/start-pilot/:token ─────────────────────────────────── */

  if (token) {
    return (
      <Container width="sm" className="py-14 sm:py-20">
        <Panel className="text-center">
          {confirmError ? (
            <>
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-danger-50 text-danger-600 ring-1 ring-inset ring-danger-100">
                <IconLock size={26} />
              </span>
              <h1 className="mt-5 font-display text-xl font-800 tracking-tight text-ink-900">
                We couldn&apos;t start that pilot
              </h1>
              <Alert tone="danger" className="mt-5 text-left">
                {confirmError}
              </Alert>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button as={Link} to={`${config.backTo}`} variant="secondary">
                  {config.backLabel}
                </Button>
                {config.signInTo && (
                  <Button as={Link} to={config.signInTo}>
                    Sign in
                  </Button>
                )}
              </div>
            </>
          ) : confirmed ? (
            <>
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-50 text-success-600 ring-1 ring-inset ring-success-100">
                <IconCheckCircle size={28} strokeWidth={1.5} />
              </span>
              <h1 className="mt-5 font-display text-xl font-800 tracking-tight text-ink-900">
                Your pilot is set up
              </h1>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-600">
                {confirmed.name ? `${confirmed.name} is ` : 'Your account is '}
                on a free pilot
                {confirmed.freeCaseAllowance
                  ? ` with ${confirmed.freeCaseAllowance} cases`
                  : ''}
                {/* Only reached when the account already had a password —
                    anyone who still needs one was sent to set it instead of
                    seeing this. */}
                . Sign in whenever you&apos;re ready.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button as={Link} to={config.pricingTo} variant="secondary">
                  See pricing
                </Button>
                <Button as={Link} to={config.signInTo || '/'}>
                  {config.signInTo ? 'Sign in' : 'Back to home'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
                <IconPackage size={26} />
              </span>
              <h1 className="mt-5 font-display text-xl font-800 tracking-tight text-ink-900">
                Setting up your pilot…
              </h1>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-600">
                One moment while we create your account.
              </p>
            </>
          )}
        </Panel>
      </Container>
    );
  }

  /* ── Request half: the form ──────────────────────────────────────────────── */

  return (
    <Container width="full" className="py-8 sm:py-12">
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-12">
        <div className="lg:pt-2">
          <p className={`text-[11px] font-700 uppercase tracking-[0.14em] ${config.accent}`}>
            {config.eyebrow}
          </p>
          <h1 className="mt-3 font-display text-[30px] font-800 leading-[1.1] tracking-tight text-ink-900 sm:text-[38px]">
            {heading[0]}
            <br />
            <span className={config.accent}>{heading[1]}</span>
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-600">{config.intro}</p>

          <ul className="mt-7 space-y-3">
            {benefits.map((benefit) => (
              <li key={benefit} className="flex items-start gap-3">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${config.dot}`}
                >
                  <IconCheck size={12} />
                </span>
                <span className="text-[15px] leading-relaxed text-ink-700">{benefit}</span>
              </li>
            ))}
          </ul>

          <p className="mt-7 border-t border-ink-200 pt-5 text-sm text-ink-500">
            Already set up?{' '}
            {config.signInTo ? (
              <Link to={config.signInTo} className="font-600 text-brand-700 underline hover:text-brand-800">
                Sign in
              </Link>
            ) : (
              <Link to="/contact?topic=courier" className="font-600 text-info-600 underline hover:text-info-700">
                Get in touch
              </Link>
            )}
          </p>
        </div>

        <Panel className="p-5 sm:p-6">
          {sent ? (
            <div className="py-4 text-center">
              <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ring-1 ring-inset ${config.chip}`}>
                <IconMail size={26} />
              </span>
              <h2 className="mt-5 font-display text-xl font-800 tracking-tight text-ink-900">
                Check your email
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-600">
                We&apos;ve sent a link to <strong>{form.email}</strong>. Follow it and your pilot
                starts straight away — nothing is created until you do.
              </p>
              <Alert tone="info" className="mt-5 text-left">
                Nothing arrived? Check your spam folder, or try again with a different address.
              </Alert>
              <Button variant="secondary" className="mt-6" onClick={() => setSent(false)}>
                Use a different address
              </Button>
            </div>
          ) : (
            <>
              <h2 className="font-display text-lg font-700 tracking-tight text-ink-900">
                {onPilot ? 'Tell us where to send it' : 'Create your account'}
              </h2>
              <p className="mt-1 text-sm text-ink-600">
                {onPilot
                  ? "No card required. We'll email you a link to confirm your address, then your pilot begins."
                  : 'Your details, then secure payment with Stripe — card details never reach ParcelBuddy.'}
              </p>

              {/* Both plans on one page: the pricing page's cards hand over a
                  ?plan= and this keeps the choice visible and changeable,
                  rather than committing them to a separate page per plan. */}
              <PlanChooser
                plans={plans}
                paidPlan={config.paidPlan}
                allowance={allowance}
                contactTo={config.contactTo}
                value={plan}
                onChange={setPlan}
              />

              {errors._general && (
                <Alert tone="danger" className="mt-4">
                  {errors._general}{' '}
                  {config.signInTo && (
                    <Link to={config.signInTo} className="font-600 underline">
                      Sign in
                    </Link>
                  )}
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={config.nameLabel} htmlFor="name" required error={errors.name}>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={set('name')}
                      placeholder={config.namePlaceholder}
                      required
                      autoFocus
                    />
                  </Field>

                  <Field label="Your name" htmlFor="contactName" error={errors.contactName}>
                    <Input
                      id="contactName"
                      value={form.contactName}
                      onChange={set('contactName')}
                      placeholder="First and last name"
                      autoComplete="name"
                    />
                  </Field>

                  <Field label="Work email" htmlFor="email" required error={errors.email}>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={set('email')}
                      placeholder="you@yourcompany.co.uk"
                      autoComplete="email"
                      required
                    />
                  </Field>

                  <Field label="Website" htmlFor="website" error={errors.website}>
                    <Input
                      id="website"
                      value={form.website}
                      onChange={set('website')}
                      placeholder="yourcompany.co.uk"
                    />
                  </Field>
                </div>

                <div className="flex flex-col gap-3 border-t border-ink-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-ink-500 sm:max-w-[16rem]">
                    {onPilot
                      ? 'Nothing is created until you follow the emailed link.'
                      : 'Payment is handled securely by Stripe. Card details never reach ParcelBuddy.'}
                  </p>
                  <Button
                    type="submit"
                    size="lg"
                    loading={loading}
                    variant={audience === 'courier' ? 'info' : 'primary'}
                    className="shrink-0"
                  >
                    {onPilot ? 'Email me the link' : 'Continue to payment'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Panel>
      </div>

      <p className="mt-8 text-center">
        <Link
          to={config.backTo}
          className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
        >
          <IconArrowLeft size={15} />
          {config.backLabel}
        </Link>
      </p>
    </Container>
  );
};

export default PilotSignupPage;
